// DailyEntryOdiaGuide — collapsible step-by-step Odia guidance card shown
// at the top of the Daily Entry page. Closed by default after first view
// (persisted in localStorage) so power users aren't slowed down.
import { useState } from "react";
import { ChevronDown, ChevronUp, BookOpen, Calendar, Users, Utensils, Save } from "lucide-react";

const STORAGE_KEY = "dailyEntryGuideCollapsed_v1";

const STEPS = [
  {
    icon: Calendar,
    titleOd: "ପଦ ୧: ବର୍ଷ ଓ ମାସ ବାଛନ୍ତୁ",
    titleEn: "Step 1: Select Year & Month",
    descOd: "ଉପର-ବାମ ପଟେ Year ଓ Month dropdown ରୁ ସଠିକ ବର୍ଷ ଏବଂ ମାସ ବାଛନ୍ତୁ।",
  },
  {
    icon: Calendar,
    titleOd: "ପଦ ୨: ତାରିଖ ବାଛନ୍ତୁ",
    titleEn: "Step 2: Pick the Date",
    descOd: "ତାରିଖ field ରୁ ଆଜିର ତାରିଖ ବା ଯେଉଁ ଦିନର entry କରିବେ ସେ ତାରିଖ ବାଛନ୍ତୁ। (Holiday ହେଲେ 'Holiday' button ଦବାନ୍ତୁ।)",
  },
  {
    icon: Users,
    titleOd: "ପଦ ୩: ଉପସ୍ଥିତ ଛାତ୍ର ସଂଖ୍ୟା ଲେଖନ୍ତୁ",
    titleEn: "Step 3: Enter Student Attendance",
    descOd: "Bal Vatika, Primary, Upper Primary, High School ର ଉପସ୍ଥିତ ଛାତ୍ର ସଂଖ୍ୟା ସଠିକ ଭାବେ ଲେଖନ୍ତୁ।",
  },
  {
    icon: Utensils,
    titleOd: "ପଦ ୪: ବ୍ୟବହାର ହୋଇଥିବା ଖାଦ୍ୟ ସାମଗ୍ରୀ ଲେଖନ୍ତୁ",
    titleEn: "Step 4: Enter Ingredients Used",
    descOd: "ଯେତେ ଚାଉଳ, ଡାଲି, ତେଲ, ସବ୍ଜି ଇତ୍ୟାଦି ବ୍ୟବହାର ହୋଇଛି ସବୁ ସଠିକ ଭାବେ ଲେଖନ୍ତୁ।",
  },
  {
    icon: Save,
    titleOd: "ପଦ ୫: Save ବଟନ ଦବାନ୍ତୁ",
    titleEn: "Step 5: Click Save",
    descOd: "ସବୁ ତଥ୍ୟ ଠିକ୍ ଅଛି ବୋଲି ଯାଞ୍ଚ କରି Save button ଦବାନ୍ତୁ। Entry ସୁରକ୍ଷିତ ହୋଇଯିବ।",
  },
];

export default function DailyEntryOdiaGuide() {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
  };

  return (
    <section
      className="mb-6 overflow-hidden rounded-2xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-sm"
      data-testid="daily-entry-odia-guide"
    >
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition hover:bg-emerald-100/50"
        data-testid="daily-entry-guide-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
            <BookOpen className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-emerald-700">
              ଓଡ଼ିଆରେ ଗାଇଡ୍ · Step-by-step guide
            </p>
            <h3 className="mt-0.5 font-heading text-base font-semibold text-emerald-900 sm:text-lg">
              Daily Entry କିପରି କରିବେ? (How to do Daily Entry)
            </h3>
          </div>
        </div>
        {collapsed ? (
          <ChevronDown className="h-5 w-5 shrink-0 text-emerald-700" />
        ) : (
          <ChevronUp className="h-5 w-5 shrink-0 text-emerald-700" />
        )}
      </button>

      {!collapsed && (
        <div className="border-t border-emerald-200 bg-white/60 px-5 py-4 sm:px-6 sm:py-5">
          <ol className="space-y-3" data-testid="daily-entry-guide-steps">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <li
                  key={i}
                  className="flex gap-3 rounded-xl border border-emerald-100 bg-white p-3 shadow-sm"
                  data-testid={`daily-entry-guide-step-${i + 1}`}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                      <div>
                        <p className="text-sm font-semibold text-emerald-900">{step.titleOd}</p>
                        <p className="text-xs font-medium text-ink-700">{step.titleEn}</p>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-ink-700">{step.descOd}</p>
                  </div>
                </li>
              );
            })}
          </ol>
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            💡 <b>Tip:</b> ଯଦି କୌଣସି ସମସ୍ୟା ଆସେ, WhatsApp ରେ +91 80934 14633 ସହିତ ଯୋଗାଯୋଗ କରନ୍ତୁ।
          </p>
        </div>
      )}
    </section>
  );
}
